import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AutocompleteInput } from "@/components/ui/autocomplete-input";
import { Trash2, BookOpen } from "lucide-react";
import { slugify } from "@/lib/utils";
import { FormFieldOption } from "./HierarchicalOptionsEditor";
import { toast } from "sonner";

export type FormFieldDependency = {
  depends_on_field_id: string | null;
  option_relations: Array<{
    option_id: string; // ID da opção do campo atual (ex: Modelo)
    related_option_id: string; // ID da opção do campo pai (ex: Escola)
  }>;
};

interface FieldDependencyEditorProps {
  currentFieldId: string | undefined;
  currentFieldOptions: FormFieldOption[];
  allFields: Array<{
    id: string;
    nome: string;
    slug: string;
    tipo: string;
    options?: FormFieldOption[];
  }>;
  dependency: FormFieldDependency | null;
  onDependencyChange: (dependency: FormFieldDependency | null) => void;
  onOptionsChange?: (options: FormFieldOption[]) => void;
}

export function FieldDependencyEditor({
  currentFieldId,
  currentFieldOptions,
  allFields,
  dependency,
  onDependencyChange,
  onOptionsChange,
}: FieldDependencyEditorProps) {
  const [dependsOnFieldId, setDependsOnFieldId] = useState<string | null>(
    dependency?.depends_on_field_id || null
  );
  const [optionRelations, setOptionRelations] = useState<
    Array<{ option_id: string; related_option_id: string }>
  >(dependency?.option_relations || []);

  // Filtrar campos disponíveis (excluir o campo atual e apenas select/multiselect/radio)
  const availableFields = useMemo(() => {
    return allFields.filter(
      (field) =>
        field.id !== currentFieldId &&
        ["select", "multiselect", "radio"].includes(field.tipo)
    );
  }, [allFields, currentFieldId]);

  // Campo selecionado como dependente
  const dependentField = useMemo(() => {
    if (!dependsOnFieldId) return null;
    return allFields.find((f) => f.id === dependsOnFieldId);
  }, [dependsOnFieldId, allFields]);

  // Opções do campo dependente (pai)
  const dependentFieldOptions = useMemo(() => {
    if (!dependentField?.options) return [];
    // Achatando opções hierárquicas para mostrar todas
    const flatten = (opts: FormFieldOption[]): FormFieldOption[] => {
      const result: FormFieldOption[] = [];
      for (const opt of opts) {
        result.push(opt);
        if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
          result.push(...flatten(opt.sub_opcoes));
        }
      }
      return result;
    };
    return flatten(dependentField.options);
  }, [dependentField]);

  // Opções do campo atual (filho) - achatadas
  const currentFieldOptionsFlat = useMemo(() => {
    const flatten = (opts: FormFieldOption[]): FormFieldOption[] => {
      const result: FormFieldOption[] = [];
      for (const opt of opts) {
        result.push(opt);
        if (opt.sub_opcoes && opt.sub_opcoes.length > 0) {
          result.push(...flatten(opt.sub_opcoes));
        }
      }
      return result;
    };
    return flatten(currentFieldOptions);
  }, [currentFieldOptions]);

  // Para cada opção do campo pai, quais opções do campo atual estão relacionadas
  const getRelatedOptionsForParent = (parentOptionId: string) => {
    return optionRelations
      .filter((rel) => rel.related_option_id === parentOptionId)
      .map((rel) => rel.option_id);
  };

  // Contar quantas vezes uma opção do campo atual está relacionada
  const getRelationCount = (optionId: string) => {
    return optionRelations.filter((rel) => rel.option_id === optionId).length;
  };

  // Toggle relacionamento
  const toggleRelation = (
    currentOptionId: string,
    parentOptionId: string
  ) => {
    const existing = optionRelations.find(
      (rel) =>
        rel.option_id === currentOptionId &&
        rel.related_option_id === parentOptionId
    );

    if (existing) {
      // Remover relacionamento
      setOptionRelations(
        optionRelations.filter(
          (rel) =>
            !(
              rel.option_id === currentOptionId &&
              rel.related_option_id === parentOptionId
            )
        )
      );
    } else {
      // Adicionar relacionamento
      setOptionRelations([
        ...optionRelations,
        { option_id: currentOptionId, related_option_id: parentOptionId },
      ]);
    }
  };

  // Adicionar novo modelo via AutocompleteInput
  const handleAddModel = (
    parentOptionId: string,
    model: { id: string; label: string; valor: string }
  ) => {
    // Verificar se já existe relacionamento
    const exists = optionRelations.some(
      (rel) =>
        rel.option_id === model.id && rel.related_option_id === parentOptionId
    );

    if (!exists) {
      setOptionRelations([
        ...optionRelations,
        { option_id: model.id, related_option_id: parentOptionId },
      ]);
      toast.success(`Modelo "${model.label}" relacionado com sucesso`);
    }
  };

  // Criar novo modelo
  const handleCreateNewModel = (
    parentOptionId: string,
    label: string
  ) => {
    if (!onOptionsChange) {
      toast.error("Não é possível criar novo modelo. Adicione opções primeiro na seção 'Opções do Campo'.");
      return;
    }

    // Criar uma nova opção temporária
    const newOption: FormFieldOption = {
      id: `temp-${Date.now()}-${Math.random()}`,
      field_id: currentFieldId || "",
      valor: slugify(label),
      label: label,
      ordem: currentFieldOptionsFlat.length,
      parent_option_id: null,
      tenant_id: "",
    };

    // Adicionar à lista de opções
    const updatedOptions = [...currentFieldOptions, newOption];
    onOptionsChange(updatedOptions);

    // Relacionar imediatamente com a escola
    setOptionRelations([
      ...optionRelations,
      { option_id: newOption.id, related_option_id: parentOptionId },
    ]);

    toast.success(`Modelo "${label}" criado e relacionado com sucesso!`);
  };

  // Quando o campo dependente muda, limpar relacionamentos
  useEffect(() => {
    if (dependsOnFieldId !== dependency?.depends_on_field_id) {
      setOptionRelations([]);
    }
  }, [dependsOnFieldId, dependency?.depends_on_field_id]);

  // Notificar mudanças
  useEffect(() => {
    if (dependsOnFieldId) {
      onDependencyChange({
        depends_on_field_id: dependsOnFieldId,
        option_relations: optionRelations,
      });
    } else {
      onDependencyChange(null);
    }
  }, [dependsOnFieldId, optionRelations, onDependencyChange]);

  if (availableFields.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Dependências</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Não há outros campos do tipo select/multiselect/radio disponíveis
            para criar dependências.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dependências</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Este campo depende de:</Label>
          <Select
            value={dependsOnFieldId || undefined}
            onValueChange={(value) =>
              setDependsOnFieldId(value === "none" ? null : value)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione um campo..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum (campo independente)</SelectItem>
              {availableFields.map((field) => (
                <SelectItem key={field.id} value={field.id}>
                  {field.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {dependsOnFieldId && dependentField && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-semibold">
                Gerenciar Relacionamentos: {dependentField.nome}
              </Label>
            </div>

            {dependentFieldOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                O campo "{dependentField.nome}" não possui opções cadastradas.
                Adicione opções primeiro.
              </p>
            ) : (
              <div className="space-y-3">
                {dependentFieldOptions.map((parentOption) => {
                  const relatedOptions = getRelatedOptionsForParent(
                    parentOption.id
                  );

                  return (
                    <Card key={parentOption.id} className="border-l-4 border-l-primary">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <BookOpen className="h-4 w-4 text-primary" />
                              <Label className="font-semibold">
                                {parentOption.label}
                                <span className="text-muted-foreground font-normal ml-2">
                                  {parentOption.valor}
                                </span>
                              </Label>
                            </div>
                            <Badge variant="secondary">
                              {relatedOptions.length} modelo(s)
                            </Badge>
                          </div>

                          {/* Modelos relacionados */}
                          {relatedOptions.length > 0 && (
                            <div className="space-y-2 pl-6">
                              {relatedOptions.map((optionId) => {
                                const option = currentFieldOptionsFlat.find(
                                  (opt) => opt.id === optionId
                                );
                                if (!option) return null;

                                const relationCount = getRelationCount(optionId);

                                return (
                                  <div
                                    key={optionId}
                                    className="flex items-center justify-between rounded-md border p-2"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        checked={true}
                                        onCheckedChange={() =>
                                          toggleRelation(
                                            optionId,
                                            parentOption.id
                                          )
                                        }
                                      />
                                      <span className="text-sm">
                                        {option.label}
                                        <span className="text-muted-foreground ml-2">
                                          {option.valor}
                                        </span>
                                      </span>
                                      {relationCount > 1 && (
                                        <Badge variant="outline" className="text-xs">
                                          {relationCount} relacionamentos
                                        </Badge>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        toggleRelation(
                                          optionId,
                                          parentOption.id
                                        )
                                      }
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* AutocompleteInput para adicionar modelos */}
                          <div className="pl-6">
                            <AutocompleteInput
                              value=""
                              onChange={() => {}}
                              onSelect={(model) =>
                                handleAddModel(parentOption.id, model)
                              }
                              onCreateNew={(label) =>
                                handleCreateNewModel(parentOption.id, label)
                              }
                              options={currentFieldOptionsFlat.map((opt) => ({
                                id: opt.id,
                                label: opt.label,
                                valor: opt.valor,
                              }))}
                              placeholder={`Adicionar modelo para ${parentOption.label}...`}
                              emptyText="Modelo não encontrado. Pressione Enter para criar novo."
                            />
                          </div>

                          {/* Lista de modelos disponíveis não relacionados */}
                          {currentFieldOptionsFlat
                            .filter(
                              (opt) => !relatedOptions.includes(opt.id)
                            )
                            .length > 0 && (
                            <div className="pl-6 space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Modelos disponíveis:
                              </Label>
                              <div className="flex flex-wrap gap-1">
                                {currentFieldOptionsFlat
                                  .filter(
                                    (opt) =>
                                      !relatedOptions.includes(opt.id)
                                  )
                                  .map((opt) => (
                                    <Button
                                      key={opt.id}
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs"
                                      onClick={() =>
                                        toggleRelation(
                                          opt.id,
                                          parentOption.id
                                        )
                                      }
                                    >
                                      + {opt.label} <span className="text-muted-foreground ml-1">{opt.valor}</span>
                                    </Button>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

